import React, { useEffect, useState } from 'react';
import Slider from 'react-slick';
import {
  ComponentRendering,
  isEditorActive,
  withSitecoreContext,
} from '@sitecore-jss/sitecore-jss-react';
import { useTranslation } from 'react-i18next';
import useMediaQuery from '../../lib/custom-hooks/useMediaQuery';
import CarouselSlide from './carouselSlide';
import {
  ContentHubSpecialOffersModel,
  ContentHubSpecialOffersSlideModel,
} from './models/specialOffers.model';
import { ContentHubSpecialOffersProps } from './types/specialOffers.types';
import useExperiencerEditorPlaceholder from '../../hooks/useExperiencerEditorPlaceholder';
import { windowService } from '../../utils/window.utils';
import { isEmptyResponse } from '../../utils/checkEmptyResponse';
import EmptyApiDataMessage from '../@shared/emptyApiDataMessage';
import ExperienceEditorComponentPlaceholder from '../@shared/experienceEditorComponentPlaceholder';
import { getCustomPaging } from '../@shared/carouselDots';
import Heading from '../@shared/heading';
import UnreleasedComponent from '../@shared/unreleasedComponent';
import AnalyticsVisibleEvent from '../@shared/analyticsVisibleEvent';
import { formatComponentNumber, googleClickSlideEvent } from '../../utils/analytics';
import CarouselArrow from '../@shared/carouselArrow';
import appendDots from '../@shared/customCarouselDots/appendDots';
import useSitecoreContext from '../../hooks/useSitecoreContext';

const ProductSpecialOffers = (props: ContentHubSpecialOffersProps) => {
  const { sitecoreContext } = useSitecoreContext();
  const specialOfferMainPageLink: string = sitecoreContext?.specialOfferPageUrl || '';
  const lineFromPageContext: string = sitecoreContext.analytics?.productLine || '';
  const lineFromParams = props.params?.lineId || '';
  const lineId =
    props.params?.enableProductParameters === '1' ? lineFromParams : lineFromPageContext;
  const { t } = useTranslation();

  const apiResponse = isEmptyResponse(props);
  const isPlaceholderShown = useExperiencerEditorPlaceholder(props);
  const isEE = isEditorActive();
  const model = new ContentHubSpecialOffersModel(props, isEE, t, specialOfferMainPageLink);
  const cardsCount = (model?.items || []).length;
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const [screenWidth, setScreenWidth] = useState(1680);
  const [activeCardIndex, updateActiveCardIndex] = useState(0);
  const [isClickedOnArrow, setIsClickedOnArrow] = useState(false);

  useEffect(() => {
    setScreenWidth(Math.min(windowService()?.innerWidth, 1680));
  }, [model]);

  useEffect(() => {
    if (!isClickedOnArrow) {
      return;
    }
    setIsClickedOnArrow(false);
    googleClickSlideEvent(
      `Slide ${formatComponentNumber(activeCardIndex + 1)}`,
      model.items[activeCardIndex]?.title?.value,
      activeCardIndex + 1
    );
  }, [activeCardIndex, isClickedOnArrow, model.items]);

  // Component is unreleased
  if (model.isUnreleased) {
    return <UnreleasedComponent />;
  }

  //  star node opened directly in EE
  if (isPlaceholderShown) {
    return (
      <ExperienceEditorComponentPlaceholder
        image={model.thumbnailEE}
        componentName={model.componentName}
      />
    );
  }

  // not opened from star node
  // data missing when navigating via from products node
  if (!apiResponse && isEditorActive()) {
    return <EmptyApiDataMessage componentName={model.componentName} />;
  }

  // No data and not in EE
  if (!apiResponse && !isPlaceholderShown && !isEditorActive()) {
    return null;
  }

  const containerPaddingLeft = 32;
  const largeImageWidth = isDesktop ? 664 : 300;
  const smallImageWidth = isDesktop ? 392 : 300;

  const totalElementsWidth =
    model.items.reduce((sumWidths: number, curr: ContentHubSpecialOffersSlideModel) => {
      if (curr.hasWideCardStyle) {
        sumWidths += largeImageWidth;
      } else {
        sumWidths += smallImageWidth;
      }
      return sumWidths;
    }, 0) + containerPaddingLeft;
  const shouldInstanceCarousel = totalElementsWidth > screenWidth;

  const arrowButtonClick = (): void => {
    setIsClickedOnArrow(true);
  };

  const settings = {
    infinite: shouldInstanceCarousel,
    dots: shouldInstanceCarousel,
    arrows: !isEditorActive() && shouldInstanceCarousel,
    variableWidth: true,
    speed: 500,
    slidesToScroll: 1,
    nextArrow: <CarouselArrow direction="next" onClickSecondary={arrowButtonClick} />,
    prevArrow: <CarouselArrow direction="previous" onClickSecondary={arrowButtonClick} />,
    customPaging: getCustomPaging,
    dotsClass: `${cardsCount <= 5 ? 'slick-dots' : ''}`,
    beforeChange: (_, index: number) => {
      updateActiveCardIndex(index);
    },
    appendDots: (dots) =>
      appendDots({ dots, cardsCount, activeCardIndex, showCustomDots: cardsCount > 5 }),
  };

  if (!model.items?.length) return null;

  return (
    <AnalyticsVisibleEvent
      rendering={props?.rendering as ComponentRendering}
      title={model?.title?.value ?? model?.eyebrow?.value ?? ''}
      template="Special Offer"
      styles={{ backgroundColor: model.backgroundColor || '#fff' }}
      className={'special-offers-wrapper'}
    >
      <div className="special-offers">
        <Heading
          eyebrow={model.eyebrow}
          subtitle={model.description}
          title={model.title}
          modelDependencies={model.modelDependencies}
          useAnalytics
          useDictionaryFallback
          eyebrowDictionaryKey={props?.eyebrowKeySpeicalOffers}
          titleDictionaryKey={props?.titleKeySpeicalOffers}
          descriptionDictionaryKey={props?.descriptionKeySpeicalOffers}
          link={model.getLink(lineId)}
        />
        <Slider {...settings}>
          {model.items?.map((item, index) => (
            <div key={item?.id} className="special-offers__slide">
              <CarouselSlide model={item} isDesktop={isDesktop} cardIndex={index + 1} />
            </div>
          ))}
        </Slider>
        <div className="special-offers__footer"></div>
      </div>
    </AnalyticsVisibleEvent>
  );
};

export default withSitecoreContext()(ProductSpecialOffers);
